import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IChecklistItem, SiteId } from '@interfaces/IScheduler';
import type { IChecklistTemplate } from '@apis/checklists';
import { Button } from '@components/ui/Button/Button';
import { DiscardModal } from '@components/ui/DiscardModal/DiscardModal';
import { PickCombo } from '@components/ui/PickCombo/PickCombo';
import { ChevronDownIcon, PencilIcon, PlusIcon, TrashIcon } from '@utils/icons';
import { useChecklistTemplates } from '../../hooks/useChecklistTemplates';
import { checkOrdinals, ordinalLabel } from '../../lib/checklistItems';
import { ChecklistEvidenceTags } from './ChecklistEvidenceTags';
import { ChecklistItemForm } from './ChecklistItemForm';
import styles from './ChecklistAuthoring.module.css';

// The checklist's editor, controlled: the caller owns the items (the drawer
// draft before the commit, a local amendment after it). A template is COPIED
// in — fresh ids, its name kept as provenance (ADR-0014) — and every item can
// then be adjusted like one typed by hand, or moved: the list is read top to
// bottom on site, so its order is part of the definition.

interface IChecklistEditorProps {
  siteId: SiteId;
  items: IChecklistItem[];
  templateName?: string;
  onChange: (items: IChecklistItem[], templateName: string | undefined) => void;
}

/** A new item: a check that asks for no evidence until told to. */
const blankItem = (): IChecklistItem => ({
  id: `ck-${crypto.randomUUID()}`,
  kind: 'check',
  content: '',
  photo: 'none',
  comment: 'none',
  blackboard: 'none',
  order: 0,
});

const renumber = (items: IChecklistItem[]): IChecklistItem[] => items.map((item, index) => ({ ...item, order: index + 1 }));

/** A template's items as this task's own. */
const fromTemplate = (template: IChecklistTemplate): IChecklistItem[] =>
  renumber(
    template.items.map((item) => ({
      id: `ck-${crypto.randomUUID()}`,
      kind: item.kind,
      content: item.content,
      photo: item.photo,
      comment: item.comment,
      blackboard: item.blackboard,
      order: 0,
    })),
  );

/** `items` with the item at `from` swapped with its neighbour at `to`. */
const swapped = (items: IChecklistItem[], from: number, to: number): IChecklistItem[] => {
  const next = [...items];
  [next[from], next[to]] = [next[to]!, next[from]!];
  return next;
};

export function ChecklistEditor({ siteId, items, templateName, onChange }: IChecklistEditorProps) {
  const { t } = useTranslation();
  const templates = useChecklistTemplates(siteId).data ?? [];
  // The item being edited: an existing one by id, or a new one ('new').
  const [editing, setEditing] = useState<{ id: string | 'new'; item: IChecklistItem } | null>(null);
  const [removing, setRemoving] = useState<IChecklistItem | null>(null);
  const [replacingWith, setReplacingWith] = useState<IChecklistTemplate | null>(null);
  const sorted = [...items].sort((a, b) => a.order - b.order);

  // The combo only offers the listed templates, so the id always resolves.
  const pickTemplate = (id: string) => {
    for (const template of templates.filter((tpl) => tpl.id === id)) {
      if (sorted.length > 0) setReplacingWith(template);
      else onChange(fromTemplate(template), template.name);
    }
  };

  const saveItem = (id: string | 'new', item: IChecklistItem) => {
    onChange(renumber(id === 'new' ? [...sorted, item] : sorted.map((i) => (i.id === id ? item : i))), templateName);
    setEditing(null);
  };

  const move = (index: number, by: -1 | 1) => onChange(renumber(swapped(sorted, index, index + by)), templateName);

  const ordinals = checkOrdinals(sorted);

  return (
    <>
      <PickCombo
        value=""
        options={templates.map((tpl) => ({ value: tpl.id, label: tpl.name, hint: t('scheduler.panel.checklist.template.items', { count: tpl.items.length }) }))}
        placeholder={t('scheduler.panel.checklist.template.placeholder')}
        onPick={pickTemplate}
        filterable
        testId="checklist-template-picker"
      />
      {templateName && <p className={styles.provenance}>{t('scheduler.panel.checklist.template.from', { name: templateName })}</p>}

      {sorted.length === 0 && editing?.id !== 'new' && (
        <p className={styles.empty}>{t('scheduler.panel.checklist.empty')}</p>
      )}

      <ol className={styles.list}>
        {sorted.map((item, index) =>
          editing?.id === item.id ? (
            <li key={item.id}>
              <ChecklistItemForm initial={editing.item} onSave={(next) => saveItem(item.id, next)} onCancel={() => setEditing(null)} />
            </li>
          ) : (
            <li key={item.id} className={styles.item}>
              <span className={styles.ordinal} aria-hidden>{ordinalLabel(ordinals.get(item.id))}</span>
              <ChecklistItemSummary item={item} />
              <span className={styles.itemActions}>
                <Button variant="ghost" size="sm" className={styles.iconBtn} aria-label={t('scheduler.panel.checklist.moveUp')} disabled={index === 0} onClick={() => move(index, -1)}>
                  <span className={styles.caretUp}><ChevronDownIcon size={14} /></span>
                </Button>
                <Button variant="ghost" size="sm" className={styles.iconBtn} aria-label={t('scheduler.panel.checklist.moveDown')} disabled={index === sorted.length - 1} onClick={() => move(index, 1)}>
                  <ChevronDownIcon size={14} />
                </Button>
                <Button variant="ghost" size="sm" className={styles.iconBtn} aria-label={t('scheduler.panel.checklist.editItem')} onClick={() => setEditing({ id: item.id, item })}>
                  <PencilIcon size={14} />
                </Button>
                <Button variant="ghost" size="sm" className={styles.iconBtn} aria-label={t('scheduler.panel.checklist.removeItem')} onClick={() => setRemoving(item)}>
                  <TrashIcon size={14} />
                </Button>
              </span>
            </li>
          ),
        )}
      </ol>

      {editing?.id === 'new' ? (
        <ChecklistItemForm initial={editing.item} onSave={(next) => saveItem('new', next)} onCancel={() => setEditing(null)} />
      ) : (
        <Button variant="ghost" size="sm" className={styles.addBtn} onClick={() => setEditing({ id: 'new', item: blankItem() })}>
          <PlusIcon size={14} />
          {t('scheduler.panel.checklist.addItem')}
        </Button>
      )}

      {removing && (
        <DiscardModal
          open
          title={t('scheduler.panel.checklist.deleteConfirm')}
          description={removing.content}
          confirmLabel={t('scheduler.panel.checklist.removeItem')}
          onCancel={() => setRemoving(null)}
          onConfirm={() => {
            onChange(renumber(sorted.filter((i) => i.id !== removing.id)), templateName);
            setRemoving(null);
          }}
        />
      )}
      {replacingWith && (
        <DiscardModal
          open
          title={t('scheduler.panel.checklist.template.replaceTitle')}
          description={t('scheduler.panel.checklist.template.replaceBody', { count: sorted.length })}
          confirmLabel={t('scheduler.panel.checklist.template.replace')}
          onCancel={() => setReplacingWith(null)}
          onConfirm={() => {
            onChange(fromTemplate(replacingWith), replacingWith.name);
            setReplacingWith(null);
          }}
        />
      )}
    </>
  );
}

/** An item's words and the evidence it asks for — the list row and the
 *  read-only view share it. */
export function ChecklistItemSummary({ item }: { item: IChecklistItem }) {
  return (
    <span className={styles.itemMain}>
      <span className={item.kind === 'description' ? styles.itemGuidance : styles.itemContent}>{item.content}</span>
      <ChecklistEvidenceTags item={item} />
    </span>
  );
}
